import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const CrewView = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What the crew view is for</Typography>
      <Typography>
        The crew view is a lean, phone-friendly screen for whoever is actually
        driving a PTS duty — today's journeys, one clear next action, and a
        direct line to the control room. It opens full-screen with no sidebar
        or admin chrome, the same way the dispatch control screen does.
      </Typography>
      <Typography>
        Open <DocLink url="/pts" text="PTS" /> directly, or from the sidebar
        under <strong>Patient Transport → My PTS Duty</strong> or the{' '}
        <strong>My PTS Duty</strong> quick link on the main dashboard.
      </Typography>
      <Alert severity="info" variant="outlined">
        No PTS permission is needed — the crew view is open to every employee
        when Patient Transport is on the subscription (an enterprise module),
        so anyone can be rostered onto a duty. Only the co-ordination screens
        (bookings, planning, dispatch and so on) need the{' '}
        <strong>Co-Ordinator</strong> permission.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Today's duty and journeys</Typography>
      <Typography>
        The top of the screen shows the assigned vehicle, shift start/end
        time, how many journeys are still open, and overall progress for the
        duty. Below that, the current journey is shown in full — patient,
        pickup, dropoff, and timing — with the next journey queued underneath.
      </Typography>
      <Typography>
        <strong>All journeys today</strong> lists every journey on the duty,
        not just the current and next one — useful for seeing how the rest of
        the day is shaping up. Under <strong>My Duties</strong>, each upcoming
        duty also expands with <strong>View planned journeys</strong>, so crew
        can read ahead before the shift starts.
      </Typography>
      <Typography>
        Tap any journey — the current one, the next one, or a row in either
        list — to open its full details: both stops with their notes and
        escorts, the requesting ward, the booking notes, patient demographics
        and next of kin, mobility category, aids and requirements, risk flags,
        allergies, medications, past medical history, and the patient's risk
        assessments. Details stay available for the rest of the shift and for
        24 hours after it ends.
      </Typography>
      <Alert severity="info" variant="outlined">
        If nothing shows up when checking well ahead of a later shift the same
        day, that's fixed — the duty appears as soon as it's scheduled for
        today, not only in a tight window around the current time.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Working a journey</Typography>
      <Typography>
        A single action button drives the journey through its stages:
        Acknowledge → Travel to pickup → Arrived at pickup → Patient onboard →
        Travel to destination → Arrived at destination → Complete handover →
        Complete journey. The button label and helper text always reflect
        whichever step comes next. Journeys dropping the patient at their own
        home skip the handover step — after arrival, the button goes straight
        to <strong>Complete journey</strong>.
      </Typography>
      <Typography>
        Any patient risk flags are shown clearly on the current journey card,
        along with the booking notes, patient notes, pickup and dropoff notes,
        and mobility category, so nothing important is missed before arrival.
        <strong> Patient details, history &amp; risk assessments</strong> opens
        the full record.
      </Typography>
      <Alert severity="warning" variant="outlined">
        Marking arrival at pickup or destination checks the device's current
        location against the journey's address. If they don't line up
        (roughly 500m or more apart), a confirmation prompt appears before the
        status is saved — this can be confirmed anyway if location isn't
        available or accurate.
      </Alert>
      <Typography>
        Where a journey has a next stop, an <strong>Open sat nav</strong>{' '}
        button hands off to Apple Maps, Google Maps, or Waze for the actual
        turn-by-turn navigation.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Risk assessment visits</Typography>
      <Typography>
        A journey booked as a <strong>Risk Assessment</strong> is a home visit
        with no patient on board — the crew travel out, complete the
        assessment, and close the visit. Its action button runs a shorter
        workflow: Acknowledge → Travel to visit → Arrived at patient home
        (shown as <strong>On scene – assessing</strong>) →{' '}
        <strong>Complete visit</strong>. There are no patient-onboard or
        handover steps. See{' '}
        <DocLink
          url="/patient-transport/risk-assessments"
          text="Risk Assessments"
          docsPage
        />{' '}
        for the assessment record itself.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">
        Adding your own jobs (dedicated duties)
      </Typography>
      <Typography>
        On a duty that's dedicated to one hospital with{' '}
        <strong>No dispatch — the crew add their own jobs</strong> turned on
        (see{' '}
        <DocLink url="/patient-transport/planning" text="Planning" docsPage />
        ), the hospital tells the crew about discharges and transfers
        directly — so nothing is booked in advance. Once clocked in, an{' '}
        <strong>Add a job</strong> button appears on the duty screen.
      </Typography>
      <Typography>
        The job form asks for the type of job, priority, and requesting ward
        or department, then the patient: look them up by hospital number, NHS
        number, or surname + date of birth (exact matches only — every search
        is logged), or create a new record with the details to follow. The
        patient's home address can be found with the address lookup, and
        mobility, bariatric, and notes complete the picture. The destination
        can be marked as not known yet — a job can't be completed until the
        outstanding details are filled in.
      </Typography>
      <Typography>
        If the new patient is travelling at the same time as a job already on
        the duty, the <strong>Travelling with</strong> choice adds the job to
        that existing run (so control see them travelling together) rather
        than starting a separate run.
      </Typography>
      <Alert severity="info" variant="outlined">
        Every crew-added job becomes a normal booking for the duty's
        organisation. On the Bookings grid it carries a <strong>Crew</strong>{' '}
        chip, and when it will be priced per journey, an amber{' '}
        <strong>Crew · review</strong> chip until a co-ordinator signs it
        off — reviewing needs the Co-Ordinator permission.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Reporting a delay</Typography>
      <Typography>
        Use <strong>Report a delay</strong> on the current journey to tell
        control room what's holding the crew up, without changing the
        journey's status. Pick the closest reason — Traffic, Ward Delay,
        Patient Not Ready, Equipment Issue, Vehicle Fault, Crew Break,
        Awaiting Handover, Prior Job Overran, or Other — and add a short note
        if it's useful.
      </Typography>
      <Alert severity="info" variant="outlined">
        The reason code (not just the note) is what reporting groups on, so
        picking the closest match — even when the note adds more detail —
        keeps delay-reason reporting meaningful across the whole fleet.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Staying in touch with control</Typography>
      <Typography>
        The chat button opens a direct conversation with the dispatcher for
        that duty. Unread messages are badged on the chat button, and a sound
        plays for new control-room messages while the tab is open.
      </Typography>
      <Typography>
        If control room reassigns, retimes, or cancels a journey on the duty,
        a banner appears with what changed, a short tone plays, and the
        browser tab title is badged with a count — even if the tab isn't
        currently focused. This relies on the tab staying open; it isn't a
        push notification to a locked phone.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Location sharing</Typography>
      <Typography>
        Location sharing is consent-based. Two things must both be true before
        the crew view shares anything: the crew member is clocked into an
        active PTS shift on the duty, <strong>and</strong> they have approved
        a location-sharing request from dispatch. Clocking in never starts
        sharing by itself — dispatch asks from the control screen, and the
        crew member approves or declines.
      </Typography>
      <Typography>
        Once approved, the crew view periodically shares the device's location
        so the vehicle appears live on the dispatch map, until the end of the
        shift or clock-out, whichever comes first. This also needs the
        browser's location permission to be allowed — the resource chip on
        the crew screen shows whether location is live, starting, blocked, or
        off.
      </Typography>
      <Alert severity="info" variant="outlined">
        The approve/decline prompt only appears in the AmbuNet mobile app —
        there is no consent prompt in the browser. A crew member working
        entirely from this web screen needs to open the mobile app once to
        answer a location-sharing request. Declining never blocks any other
        part of the crew workflow, and dispatch can always ask again.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Leaving the crew view</Typography>
      <Typography>
        Use the back arrow next to "Patient Transport" at the top of the
        screen to return to the main AmbuNet dashboard.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Using the native mobile app instead</Typography>
      <Typography>
        Crew can also work their duty from the native AmbuNet mobile app
        rather than a browser — see{' '}
        <DocLink
          url="/patient-transport/mobile-app"
          text="Mobile App"
          docsPage
        />{' '}
        for what's the same and what's different (notably: no push
        notification for control-room changes yet, unlike this browser
        screen's in-tab alert).
      </Typography>
    </Stack>
  </Stack>
)

export default CrewView
